import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/base/ComicCard";
import ChapterList from "@/pages/detail/components/ChapterList";
import useFavorites from "@/hooks/useFavorites";
import { buildChapters, formatViews, getComicById, getRelatedComics } from "@/utils/manga";
import { getProgress } from "@/utils/storage";
import usePageMeta, { SITE_NAME } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { comicId } = useParams();
  const comic = comicId ? getComicById(comicId) : undefined;
  const { isFav, toggle } = useFavorites();

  usePageMeta(
    comic ? `${comic.title} - ${SITE_NAME}` : `作品未找到 - ${SITE_NAME}`,
    comic ? comic.description : "抱歉，没有找到对应的漫画作品。",
  );

  if (!comic) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-4xl mb-5">
            <i className="ri-file-unknow-line"></i>
          </span>
          <h1 className="font-heading font-bold text-2xl text-foreground-950">
            没有找到这部漫画
          </h1>
          <p className="text-sm text-foreground-500 mt-3 max-w-md">
            它可能已被移除或链接有误，不如回到分类页看看其他精彩作品。
          </p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            浏览全部漫画
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const chapters = buildChapters(comic);
  const firstChapter = chapters[0];
  const related = getRelatedComics(comic, 4);
  const progress = getProgress();
  const lastReadId = progress[comic.id];
  const resumeChapter = lastReadId
    ? chapters.find((item) => item.id === lastReadId)
    : undefined;
  const favorite = isFav(comic.id);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category?cat=${comic.category}`} className="hover:text-accent-600">
            漫画分类
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">{comic.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
          <div className="w-full lg:w-80 shrink-0">
            <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={comic.cover}
                alt={`${comic.title} 韩漫封面`}
                title={`${comic.title} 无删减韩漫在线阅读`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                {comic.status}
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                更新至 {comic.episodes} 话
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                {comic.schedule}
              </span>
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
              {comic.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mt-1.5">
              {comic.subTitle}
            </p>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-foreground-400"></i>
                作者：{comic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-foreground-400"></i>
                更新：{comic.updatedAt}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-foreground-400"></i>
                {formatViews(comic.views)} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500"></i>
                {comic.rating.toFixed(1)} 评分
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-700 border border-background-200"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6">
              <h2 className="font-heading font-semibold text-base text-foreground-950 mb-2">
                作品简介
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {comic.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <Link
                to={`/read/${comic.id}/${resumeChapter ? resumeChapter.id : firstChapter.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-book-read-line"></i>
                {resumeChapter ? `继续阅读 ${resumeChapter.title}` : "开始阅读"}
              </Link>
              <button
                type="button"
                onClick={() => toggle(comic.id)}
                className={`inline-flex items-center gap-2 h-11 px-6 rounded-md border text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  favorite
                    ? "border-secondary-400 bg-secondary-50 text-secondary-700"
                    : "border-background-300 text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                {favorite ? "已收藏" : "加入收藏"}
              </button>
              <Link
                to={`/category?cat=${comic.category}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <ChapterList
            comicId={comic.id}
            chapters={chapters}
            lastReadId={lastReadId}
          />
        </div>

        <section className="mt-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
            相关推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}